import { HttpClient } from "@angular/common/http";
import { Inject, Injectable, PLATFORM_ID } from "@angular/core";
import { FormlyFieldConfig } from "@ngx-formly/core";
import { Observable } from "rxjs";
import { catchError, finalize, map, startWith, switchMap } from "rxjs/operators";
import { AbstractService } from "../../abstractions/abstract-typed.service";
import { Globals } from "../../app.globals";
import { Project } from "../projects/projects.service";
import { Client } from "../shared/client.service";

@Injectable()
export class FormsService extends AbstractService<Form> {
    formArea: string = "";

    constructor(
        public http: HttpClient,
        public globals: Globals,
        @Inject(PLATFORM_ID) private platformId: object
    ) {
        super(http, globals);
    }

    deactivate(entryNumber: number): Observable<any> {
        this.showSpinner();
        return this.http
            .put(
                this.getApiUrl() +
                "/deactivate/" +
                this.formArea +
                "/" +
                entryNumber,
                null
            )
            .pipe(
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    activate(entryNumber: number): Observable<any> {
        this.showSpinner();
        return this.http
            .put(
                this.getApiUrl() +
                "/activate/" +
                this.formArea +
                "/" +
                entryNumber,
                null
            )
            .pipe(
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    setDefault(entryNumber: number): Observable<any> {
        this.showSpinner();
        return this.http
            .put(
                this.getApiUrl() +
                "/default/" +
                this.formArea +
                "/" +
                entryNumber,
                null
            )
            .pipe(
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    setNonDefault(entryNumber: number): Observable<any> {
        this.showSpinner();
        return this.http
            .put(
                this.getApiUrl() +
                "/nondefault/" +
                this.formArea +
                "/" +
                entryNumber,
                null
            )
            .pipe(
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    addSection(section: Section): Observable<any> {
        this.showSpinner();
        return this.http
            .post(this.getApiUrl() + "/sections/" + this.formArea, section)
            .pipe(
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    getForm(formId: number): Observable<any> {
        return this.get(formId);
    }

    async getAllActiveOfType(
        formArea: string,
        formType: string,
        projectLevel: boolean,
        projectId: number,
        parentFormArea: string = null
    ): Promise<Form[]> {
        this.showSpinner();

        try {
            var results = await this.http
                .get<Form[]>(
                    this.getApiUrl() +
                    "/getActive/type/" +
                    formArea +
                    "/" +
                    formType +
                    "/" +
                    projectLevel +
                    "/" +
                    projectId +
                    (parentFormArea
                        ? "?parentFormArea=" + parentFormArea
                        : "")
                )
                .toPromise();

            return results;
        } catch (error) {
            this.handleError(error);
        } finally {
            this.hideSpinner();
        }
    }

    getAllFormList(
        formArea: string,
        formType: string,
        projectLevel: boolean,
        projectId: number,
        parentFormArea: string = null):
        Observable<Form[]> {
        this.showSpinner();

        return this.http
            .get<Form[]>(
                this.getApiUrl() +
                "/getActive/type/" +
                formArea +
                "/" +
                formType +
                "/" +
                projectLevel +
                "/" +
                projectId +
                (parentFormArea
                    ? "?parentFormArea=" + parentFormArea
                    : "")
            )
            .pipe(
                map((res) => {
                    let data = res as Form[];
                    return data;
                }),
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }


    getAllActive(
        pushToOfflineStorage: boolean = false,
        withQuestions: boolean = true
    ): Observable<Form[]> {
        this.showSpinner();

        return this.http
            .get(this.getApiUrl() + "/getActive/" + withQuestions)
            .pipe(
                map((res) => {
                    let data = res as Form[];
                    return data;
                }),
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    getAllActiveForProject(projectId: number): Observable<Form[]> {
        this.showSpinner();

        this.getAllMetadataForActiveForms(projectId).subscribe();

        return this.http.get(this.getApiUrl() + "/getActive/true").pipe(
            map((res) => {
                let data = res as Form[];
                return data;
            }),
            catchError(this.handleError),
            finalize(() => this.hideSpinner())
        );
    }

    getFormMetadata(
        projectId: number,
        settingsFormEntryNumber: number,
        completedFormEntryNumber: number
    ): Observable<FormlyFieldConfig[]> {
        this.showSpinner();

        return this.http
            .get(
                this.getApiUrl() +
                "/" +
                this.formArea +
                "/" +
                projectId +
                "/metadata/" +
                settingsFormEntryNumber +
                (completedFormEntryNumber
                    ? "?completedFormEntryNumber=" +
                    completedFormEntryNumber
                    : "")
            )
            .pipe(
                map((res) => res as FormlyFieldConfig[]),
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    getAllMetadataForActiveForms(projectId: number): Observable<any> {
        return this.http
            .get(this.getApiUrl() + "/all/metadata/" + projectId)
            .pipe(
                map((res) => res),
                catchError(this.handleError)
            );
    }

    getAllSections(formType: string): Observable<Section[]> {
        this.showSpinner();
        return this.http
            .get(
                this.getApiUrl() +
                "/" +
                this.formArea +
                "/" +
                formType +
                "/sections"
            )
            .pipe(
                map((res) => res as Section[]),
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    updateFileDescription(fileName, fileDescription) {
        let result = {
            path: fileName,
            description: fileDescription,
        };
        this.showSpinner();
        return this.http.put("/api/projects/update/description/", result).pipe(
            map((res) => {
                return res;
            }),
            catchError(this.handleError),
            finalize(() => this.hideSpinner())
        );
    }

    save(
        projectId: number,
        entity: any,
        activePageOrder: number = 1,
        isFromPaging: boolean = false
    ): Observable<any> {
        this.showSpinner();
        entity.isAdded = false;
        entity.isUpdated = false;
        let beforeSubmitDate = entity.date
        return this.http
            .put(
                "/api/projects/" +
                projectId +
                "/forms/" +
                this.formArea +
                "/submit" +
                "?isFromPaging=" +
                isFromPaging +
                "&activePageOrder=" +
                activePageOrder,
                this.mapSave(entity)
            )
            .pipe(
                map((res) => {
                    var data = res as any;
                    entity.entryNumber = data.entryNumber;
                    entity.id = data.id;
                    //timezone value is added in entity.date field in every paging request
                    //which causes the entity.date to constant positive shift of timezone 
                    //i.e. 2021-11-01 11:00:00 + 05:30 => 2021-11-01 16:30:00 which ultimately increment date by +1
                    //to counter this we are balancing it back to its initial for value 
                    if (beforeSubmitDate < entity.date && isFromPaging)
                        entity.date = new Date(entity.date.getTime() + entity.date.getTimezoneOffset() * 60000)
                    entity.created = entity.date;
                    entity.createdByFullName = entity.enteredBy;

                    return data;
                }),
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    deleteFile(fileName): Observable<void> {
        this.showSpinner();

        return this.http
            .request("delete", this.getApiUrl() + "/file/delete", {
                body: { Url: fileName },
            })
            .pipe(
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    deleteFiles(fileNames: string[]): Observable<void> {
        return this.http
            .request("delete", this.getApiUrl() + "/file/deletes", {
                body: fileNames,
            })
            .pipe(
                catchError(this.handleError)
            );
    }

    copy(projectId: number, id: number): Promise<CopyForm> {
        this.showSpinner();
        return this.http
            .post(
                "/api/projects/" +
                projectId +
                "/forms/" +
                this.formArea +
                "/copy/" +
                id,
                null
            )
            .pipe(
                map((res) => {
                    var data = res as any;

                    return data;
                }),
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            )
            .toPromise();
    }

    getDocuments(
        projectId: number,
        aggregateId: string,
        isImage: boolean,
        uploaderId: string = null,
        formEntryNumber: number
    ): Observable<any[]> {
        this.showSpinner();
        return this.http
            .get(
                this.getApiUrl() +
                "/" +
                this.formArea +
                "/document/" +
                projectId +
                "/" +
                aggregateId +
                "/" +
                uploaderId +
                (isImage ? "/true" : "/false") +
                "/" +
                formEntryNumber
            )
            .pipe(
                map((res) => res as any[]),
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    getBulkDocuments(
        projectId: number,
        aggregateIds: string[]
    ): Observable<BulkDocumentResponse[]> {
        this.showSpinner();
        return this.http
            .post(this.getApiUrl() + "/document/" + projectId, aggregateIds)
            .pipe(
                map((res) => res as Document[]),
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    getApiUrl(): string {
        return "/api/settings/forms";
    }

    getAllAnswers(
        projectId: number,
        formEntryNumber: number,
        filterQuestion: string,
        filters: {},
        startDate: string,
        endDate: string,
        isFromRegisterFilter: boolean
    ): Observable<any> {
        return this.http
            .post<any>(
                "/api/projects/" +
                projectId +
                "/forms/" +
                this.formArea +
                "/getall/" +
                formEntryNumber +
                "/" +
                startDate +
                "/" +
                endDate +
                "?filterQuestion=" +
                filterQuestion +
                "&isFromRegisterFilter=" +
                isFromRegisterFilter,
                { filters: filters, downloadFormIds: "" }
            )
            .pipe(
                map((res) => res),
                catchError(this.handleError)
            );
    }

    getAnswer(
        projectId: number,
        settingsFormEntryNumber: number,
        completedFormEntryNumber: number,
        showSpinner: boolean = true
    ): Observable<any> {
        if (showSpinner)
            this.showSpinner();
        return this.http
            .get(
                "/api/projects/" +
                projectId +
                "/forms/" +
                this.formArea +
                "/" +
                settingsFormEntryNumber +
                "/get/" +
                completedFormEntryNumber
            )
            .pipe(
                map((res) => res),
                catchError(this.handleError),
                finalize(() => {
                    if (showSpinner) this.hideSpinner()
                })
            );
    }

    getAnswers(
        projectId: number,
        settingsFormEntryNumber: number,
        entryNumbers: number[]
    ): Observable<any> {
        this.showSpinner();
        return this.http
            .post(
                "/api/projects/" +
                projectId +
                "/forms/" +
                this.formArea +
                "/" +
                settingsFormEntryNumber +
                "/getMultiple",
                entryNumbers
            )
            .pipe(
                map((res) => res),
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    download(
        projectId: number,
        formArea: string,
        settingsFormEntryNumber: number,
        pdfType: string,
        fileName: string,
        imageQuality: string,
        isPreview: boolean,
        entryNumbers: number[]
    ): Observable<any> {
        return this.http
            .post(
                "/api/projects/" +
                projectId +
                "/" +
                formArea +
                "/" +
                settingsFormEntryNumber +
                "/" +
                pdfType +
                "/" +
                fileName +
                "/" +
                isPreview +
                "/downloadPdf" +
                "?imageQuality=" +
                imageQuality,
                entryNumbers
            )
            .pipe(
                map((res) => res),
                catchError(this.handleError)
            );
    }

    downloadRegister(
        projectId: number,
        formArea: string,
        settingsFormEntryNumber: number,
        fileName: string,
        orientation: string,
        pageSize: string,
        isPreview: boolean,
        filterQuestion: string,
        startDate: string,
        endDate: string,
        answerIds: any[]
    ): Observable<any> {
        return this.http
            .post(
                "/api/projects/" +
                projectId +
                "/" +
                formArea +
                "/" +
                settingsFormEntryNumber +
                "/" +
                fileName +
                "/" +
                pageSize +
                "/" +
                orientation +
                "/" +
                isPreview +
                "/" +
                startDate +
                "/" +
                endDate +
                "/downloadPdfRegister" +
                "?filterQuestion=" +
                filterQuestion,
                answerIds
            )
            .pipe(
                map((res) => res),
                catchError(this.handleError)
            );
    }

    checkPdfComplete(projectId: number, fileId: string): Observable<any> {
        if (projectId == null) projectId = 0;
        return this.http
            .get("/api/projects/checkPdfComplete/" + projectId + "/" + fileId)
            .pipe(
                map((res) => res),
                catchError(this.handleError)
            );
    }

    pdfDownloadStatus(executionArn: string): Observable<any> {
        return this.http
            .get("/api/projects/pdfdownloadstatus/" + executionArn)
            .pipe(
                map((res) => res),
                catchError(this.handleError)
            );
    }

    stopPDFDownload(executionArn: string): Observable<any> {
        return this.http
            .get("/api/projects/pdfdownloadstop/" + executionArn)
            .pipe(
                map((res) => res),
                catchError(this.handleError)
            );
    }

    checkPdfRegisterComplete(
        projectId: number,
        fileId: string
    ): Observable<any> {
        if (projectId == null) projectId = 0;
        return this.http
            .get(
                "/api/projects/checkpdfregistercomplete/" +
                projectId +
                "/" +
                fileId
            )
            .pipe(
                map((res) => res),
                catchError(this.handleError)
            );
    }

    private mapSave(value: any) {
        var result: SaveForm = {
            aggregateRootId: value.aggregateRootId,
            answers: [],
            client: value.client,
            cores: value.cores,
            date: value.date,
            displayGlobalId: value.displayGlobalId,
            elements: value.elements,
            elevations: value.elevations,
            enteredBy: value.enteredBy,
            entryNumber: value.entryNumber,
            formId: value.formId,
            globalEntryNumber: value.globalEntryNumber,
            hasDefaultAttachments: value.hasDefaultAttachments,
            id: value.id,
            isPublic: value.isPublic,
            levels: value.levels,
            packages: value.packages,
            projectId: value.projectId,
            structureReferences: value.structureReferences,
            projectName: value.projectName,
            projectAddress: value.projectAddress,
            formEmbedContacts: value.formEmbedContacts,
            modifiedPageOrders: value.modifiedPageOrders,
            formLogicallyHiddenQuestions: value.formLogicallyHiddenQuestions,
            formGridCellAnswers: [],
            formGridSummaries: []
        };

        for (let prop in value) {
            let splitProp = prop.split("_");

            if (splitProp.length == 2) {
                let answer = new SaveAnswer();
                answer.formId = value.id;
                answer.questionId = +splitProp[1];
                answer.answers = value[prop];
                result.answers.push(answer);
            }
            if (splitProp.length == 3) {
                if (prop.indexOf("_emailText") > -1 ||
                    prop.indexOf("_signed") > -1) {
                    let answer = new SaveAnswer();
                    answer.formId = value.id;
                    answer.questionId = +splitProp[1];
                    answer.answers = value[prop];
                    if (prop.indexOf("_emailText") > -1)
                        answer.isEmailText = true;
                    if (prop.indexOf("_signed") > -1)
                        answer.isSigned = true;
                    result.answers.push(answer);
                }
            }
        }

        for (let prop in value) {
            let splitProp = prop.split("_");
            let answer = result.answers.find(
                (s) => s.questionId == +splitProp[1]
            );
            if (
                (prop.indexOf("_stopnoti") > -1 ||
                    prop.indexOf("_user") > -1) &&
                !answer
            ) {
                let newanswer = new SaveAnswer();
                newanswer.formId = value.formId;
                newanswer.questionId = +splitProp[1];
                newanswer.answers = null;
                result.answers.push(newanswer);
                answer = newanswer;
            }
            else if (prop.indexOf("_name") > -1 && answer) {
                let index = result.answers.findIndex((x) => x.questionId == +splitProp[1] && !x.isEmailText && !x.isSigned);

                if (result.answers[index]) {
                    result.answers[index].answers = value[prop];
                }
            }
            else if (prop.endsWith("_grid")) {
                if ((value[prop] || {}).cells || [].length > 0) {
                    (value[prop] || {}).cells.forEach(x => {
                        if (Array.isArray(x.value)) {
                            x.value = x.value.join(',');
                        }

                        const gridCellAnswer: FormGridCellAnswer = {
                            id: 0,
                            gridCellId: x.cellId,
                            formId: value.formId,
                            gridCellQuestionId: x.gridCellQuestionId,
                            answer: x.value
                        };
                        result.formGridCellAnswers.push(gridCellAnswer);
                    });
                    let answer = new SaveAnswer();
                    answer.formId = value.formId;
                    answer.questionId = +splitProp[1];
                    answer.answers = ['yes'];
                    answer.totalScore = value[prop].totalScore;
                    answer.maxScore = value[prop].maxScore;
                    result.answers.push(answer);
                } else {
                    //result.formGridCellAnswers = null;
                }
            }
            else if (prop.endsWith("_gridSummary")) {
                let answer = new SaveAnswer();
                answer.formId = value.formId;
                answer.questionId = +splitProp[1];
                answer.answers = ['yes'];

                const gridSummary: FormGridSummary = {
                    id: 0,
                    questionId: +splitProp[1],
                    formId: value.formId,
                    totalScore: value[prop].totalScore,
                    maxScore: value[prop].maxScore,
                    ratingPercentage: value[prop].ratingPercentage,
                    ratingLabel: value[prop].ratingLabel,
                    overallComments: value[prop].overallComments
                };

                result.formGridSummaries.push(gridSummary);
                result.answers.push(answer);
            }
            else if (!answer) {
                continue;
            }
            if (splitProp.length == 3) {
                if (prop.indexOf("other") > -1) {
                    answer.other = value[prop];
                }
                if (prop.indexOf("_stopnoti") > -1) {
                    answer.stopNotification = value[prop];
                } else if (prop.indexOf("_user") > -1) {
                    if (value[prop].length > 0) {
                        answer.notificationUsers = [];
                        answer.notificationUsers.push(value[prop]);
                    } else {
                        answer.notificationUsers = null;
                    }
                }
            } else if (splitProp.length === 4) {
                answer.signatureByThirdParty = value[prop];
            }
        }

        return result;
    }

    convertToFileNameFormatArray(
        fileNameFormat: string,
        setup: Form
    ): FileNameFormatItem[] {
        fileNameFormat = fileNameFormat || FileNameFormatItem.default;
        var result: FileNameFormatItem[] = [];
        var fileNameParts = [];
        let options = this.getAutoCompleteList(setup, null);

        if (!Array.isArray(fileNameFormat)) {
            fileNameParts = fileNameFormat.split("|");
        }

        for (let fileNamePart of fileNameParts) {
            let parts = fileNamePart.split(":");

            if (parts[0] == FileNameFormatItemType.Text)
                result.push({
                    label: parts[1],
                    value: parts[1],
                    type: FileNameFormatItemType.Text,
                });
            else {
                let option = options.find(
                    (x) => x.type == parts[0] && x.value == parts[1]
                );

                if (option) result.push(option);
            }
        }

        return result;
    }

    convertFromFileNameFormatArray(
        fileNameFormatArray: FileNameFormatItem[]
    ): string {
        return fileNameFormatArray.map((x) => `${x.type}:${x.value}`).join("|");
    }

    getFileName(
        setup: Form,
        project: Project,
        client: Client,
        entity: SaveForm
    ): string {
        var fileNameParts = this.convertToFileNameFormatArray(
            setup.fileNameFormat as string,
            setup
        );
        var fileName = "";
        var partOfFileName = "";
        for (var fileNamePart of fileNameParts) {
            switch (fileNamePart.type) {
                case FileNameFormatItemType.Client:
                    partOfFileName = client[fileNamePart.value];
                    break;
                case FileNameFormatItemType.Project:
                    partOfFileName = project[fileNamePart.value];
                    break;
                case FileNameFormatItemType.Text:
                    partOfFileName = fileNamePart.value;
                    break;
                case FileNameFormatItemType.DateField:
                    partOfFileName = this.convertDate(
                        entity[fileNamePart.value]
                    );
                    break;
                case FileNameFormatItemType.Field:
                    if (fileNamePart.value == "entryNumber")
                        partOfFileName =
                            "" +
                            entity[
                            setup.displayGlobalId
                                ? "globalEntryNumber"
                                : "entryNumber"
                            ];
                    else partOfFileName = entity[fileNamePart.value];
                    break;
                case FileNameFormatItemType.Setup:
                    partOfFileName = setup[fileNamePart.value];
                    break;
            }

            fileName += partOfFileName || "";
        }

        return fileName;
    }

    getAutoCompleteList(setup: Form, custom?: string): FileNameFormatItem[] {
        var results: FileNameFormatItem[] = [
            {
                label: "Form Title",
                value: "title",
                type: FileNameFormatItemType.Setup,
            },
            {
                label: "Created",
                value: "created",
                type: FileNameFormatItemType.DateField,
            },
            {
                label: "Entry Number",
                value: "entryNumber",
                type: FileNameFormatItemType.Field,
            },
            {
                label: "Project Name",
                value: "name",
                type: FileNameFormatItemType.Project,
            },
            {
                label: "Project Code",
                value: "code",
                type: FileNameFormatItemType.Project,
            },
            {
                label: "Organisation",
                value: "organizationName",
                type: FileNameFormatItemType.Project,
            },
            {
                label: "Client Name",
                value: "name",
                type: FileNameFormatItemType.Client,
            },
        ];

        if (setup.askCores)
            results.push({
                label: "Core",
                value: "core",
                type: FileNameFormatItemType.Field,
            });

        if (setup.askElement)
            results.push({
                label: "Element",
                value: "element",
                type: FileNameFormatItemType.Field,
            });

        if (setup.askElevation)
            results.push({
                label: "Elevation",
                value: "elevation",
                type: FileNameFormatItemType.Field,
            });

        if (setup.askEnteredBy)
            results.push({
                label: "Entered By",
                value: "createdByFullName",
                type: FileNameFormatItemType.Field,
            });

        if (setup.askLevel)
            results.push({
                label: "Level",
                value: "level",
                type: FileNameFormatItemType.Field,
            });

        if (setup.askPackage)
            results.push({
                label: "Package",
                value: "package",
                type: FileNameFormatItemType.Field,
            });

        if (setup.askProgress)
            results.push({
                label: "Progress",
                value: "progress",
                type: FileNameFormatItemType.Field,
            });

        if (setup.askStructureReference)
            results.push({
                label: "Structure Reference",
                value: "structureReference",
                type: FileNameFormatItemType.Field,
            });

        if (setup.askClient)
            results.push({
                label: "Client",
                value: "client",
                type: FileNameFormatItemType.Field,
            });

        if (setup.askProjectName)
            results.push({
                label: "ProjectName",
                value: "projectName",
                type: FileNameFormatItemType.Field,
            });

        if (setup.askProjectAddress)
            results.push({
                label: "Project Address",
                value: "projectAddress",
                type: FileNameFormatItemType.Field,
            });

        results.sort((a, b) => {
            let left = a.label.toLowerCase();
            let right = b.label.toLowerCase();

            if (left < right) return -1;
            if (left > right) return 1;

            return 0;
        });

        if (custom)
            results.push({
                label: 'Custom text: "' + custom + '"',
                value: custom,
                type: FileNameFormatItemType.Text,
            });

        return results;
    }

    getAttachments(
        formArea: string,
        setupFormEntryNumber: number
    ): Promise<any> {
        this.showSpinner();
        return this.http
            .get(
                "/api/settings/forms/" +
                this.projectId +
                "/" +
                formArea +
                "/get-attachments/" +
                setupFormEntryNumber
            )
            .pipe(
                map((res) => res),
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            )
            .toPromise();
    }

    getAttachmentsByDefaultAttachmentPrefix(
        formArea: string,
        defaultAttachmentPrefix: string
    ): Promise<any> {
        this.showSpinner();
        return this.http
            .get(
                "/api/settings/forms/" +
                this.projectId +
                "/" +
                formArea +
                "/get-attachments-by-prefix/" +
                defaultAttachmentPrefix
            )
            .pipe(
                map((res) => res),
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            )
            .toPromise();
    }

    delete(id: number): Observable<void> {
        this.showSpinner();
        return this.http
            .delete(this.getApiUrl() + "/" + this.formArea + "/" + id)
            .pipe(
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    get(id: number): Observable<Form> {
        this.showSpinner();
        return this.http
            .get(
                this.getApiUrl() +
                (this.projectId ? "/" + this.projectId : "") +
                "/" +
                this.formArea +
                "/" +
                id
            )
            .pipe(
                map((res) => res as Form),
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    async clone(id: number): Promise<any> {
        this.showSpinner();
        return this.http
            .post(this.getApiUrl() + "/clone/" + id, null)
            .pipe(
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            )
            .toPromise();
    }

    getLinkedFormSetups(
        formArea: string,
        projectId: number
    ): Promise<GetLinkedFormSetupResponse[]> {
        this.showSpinner();
        return this.http
            .get(
                "/api/settings/forms/linked-form-setups/" +
                formArea +
                "/" +
                projectId
            )
            .pipe(
                map((res) => res),
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            )
            .toPromise();
    }

    getLinkedProjectSetups(
        formId: number
    ): Promise<GetLinkedFormSetupResponse[]> {
        this.showSpinner();
        return this.http
            .get("/api/settings/forms/linked-form-setups/" + formId)
            .pipe(
                map((res) => res),
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            )
            .toPromise();
    }

    saveLinkedFormSetups(request: SaveLinkedFormSetup[]): Promise<void> {
        this.showSpinner();
        return this.http
            .post("/api/settings/forms/linked-form-setups/", request)
            .pipe(
                map((res) => res),
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            )
            .toPromise();
    }

    uploadFile(projectId: number, formArea: string, formId: number, formData: FormData): Observable<any> {
        this.showSpinner();
        return this.http.put("/api/projects/" +
            projectId +
            "/forms/" +
            formArea +
            "/upload/" +
            formId, formData).pipe(
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    downloadCSV(
        projectId: number,
        formArea: string,
        formId: number,
        downloadWithData: boolean,
        isDownloadMoreThan200: boolean,
        exportEntryIds: number[]
    ): Observable<any> {
        this.showSpinner();
        return this.http.post<any>("/api/projects/" +
            projectId +
            "/forms/" +
            formArea +
            "/download/" +
            formId +
            "/" +
            downloadWithData +
            "/" +
            isDownloadMoreThan200,
            exportEntryIds,
            {
                responseType: 'blob' as 'json'
            }).pipe(
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    downloadLookupValuesCSV(projectId: number, formArea: string, formId: number): Observable<any> {
        this.showSpinner();
        return this.http.get<Blob>("/api/projects/" +
            projectId +
            "/forms/" +
            formArea +
            "/downloadlookupvalues/" +
            formId,
            { responseType: 'blob' as 'json' }).pipe(
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }
}

export interface SaveLinkedFormSetup {
    formId: number;
    isActive: boolean;
    projectId: number;
}

export interface GetLinkedFormSetupResponse {
    formId: number;
    modified: Date;
    modifiedBy: string;
    projectId: number;
    publicAccessToken: string;
    isActive: boolean;
    accessType: string;
    status: string;
    isDefault: string;
}

export class CopyForm {
    globalEntryNumber: number;
    projectId: number;
}

export class SaveForm {
    aggregateRootId: string = "00000000-0000-0000-0000-000000000000";
    client: string;
    cores: string;
    created?: Date;
    date: Date;
    displayGlobalId: boolean;
    elements: string;
    elevations: string;
    enteredBy: string;
    entryNumber: number;
    formId: number;
    globalEntryNumber: number;
    hasDefaultAttachments: boolean;
    id: number;
    isPublic: boolean;
    levels: string;
    packages: string;
    progress?: number;
    projectId: number;
    structureReferences: string;
    projectName: string;
    projectAddress: string;
    answers: SaveAnswer[];
    files?: any;
    images?: any;
    modifiedPageOrders: number[];
    formEmbedContacts?: FormEmbedContact[];
    formLogicallyHiddenQuestions?: FormLogicallyHiddenQuestion[];
    formGridCellAnswers?: FormGridCellAnswer[];
    formGridSummaries?: FormGridSummary[];
}

class SaveAnswer {
    answers: any[];
    formId: number;
    id: number;
    other: string;
    questionId: number;
    signatureByThirdParty: string;
    stopNotification: boolean;
    notificationUsers: any[];
    isEmailText: boolean;
    isSigned: boolean;
    totalScore: number;
    maxScore: number;
    ratingPercentage: number;
    ratingLabel: string;
    overallComments: string;
}

export class SaveDocument {
    formId: number;
    questionId: number;
    type: string;
    fileName: string;
    originalFileName: string;
    fileUrl: string;
    addedBy: string;
    uploadedBy: string;
    displayOnTable: boolean;
    date: Date;
    uploadedByEmployeeNumber: string;
}

export class Form {
    askProjectName: boolean;
    askProjectAddress: boolean;
    askClient: boolean;
    askCores: boolean;
    askDate: boolean;
    askElement: boolean;
    askElevation: boolean;
    askEnteredBy: boolean;
    askLevel: boolean;
    askPackage: boolean;
    askProgress: boolean;
    askStructureReference: boolean;
    created: Date;
    createdBy: string;
    createdById: number;
    defaultAttachmentPrefix: string;
    displayGlobalId: boolean;
    displayInProjects: boolean;
    displayInEstimatingProjects: boolean;
    displayInQSProjects: boolean;
    displayInProjectsLocations: boolean;
    downloadOption: string;
    entryNumber?: number;
    fileNameFormat?: string | FileNameFormatItem[];
    formArea: string;
    formType: string;
    hasDefaultAttachments: boolean;
    id?: number;
    isActive: boolean;
    isDefault: boolean;
    isPublic: boolean;
    modified?: Date;
    modifiedById: number;
    publicAccessToken: string;
    questions: Question[] = [];
    section?: Section;
    sectionId?: number;
    title: string;
    subQuestions: Question[];
    isAllowUploadedCSV: boolean;
    isDisplayEntry: boolean;
    isUpdateCoreQuestionCSV: boolean;
    notificationEmbedContacts: NotificationEmbedContact[];
    logicalOperator: number;
    subpageLogicalVisibilityState: number;
    pageOrientation: string;
}

export class Question {
    allowOther: boolean;
    extendedType?: string;
    formId?: number;
    id?: number;
    // Client-side only: flags this question's row red (even collapsed) when it
    // failed save validation, instead of popping a modal - see FormsForm.isValid().
    hasError?: boolean;
    validationMessage?: string;
    // Client-side only: for a Notification/Contacts question, which specific settings
    // field (e.g. "selectedRoleList") the validationMessage is about, so that exact
    // field can be highlighted too, not just the question's row - see
    // FormsForm.validateContactQuestion().
    validationField?: string;
    isRemembered: boolean;
    isRequired: boolean;
    isRequiredPageChange: boolean;
    label: string;
    totalOptions: number;
    roles: any[] = [];
    options: Option[];
    selectedNotificationUsers: any[];
    notificationUsers: NotificationUser[];
    notificationOptions: NotificationOption[];
    notificationAddRoles: NotificationAddRole[] = [];
    selectedRoleList: any[] = [];
    selectedNotificationRecepients: any[];
    selectedDisplayInFormAs: any[];
    selectedNotificationContactIds: any[];
    displayInFormAs: string;
    order: number;
    notificationAction: boolean;
    notificationView: boolean;
    emailSubject: string;
    emailBodyTextOption: string;
    emailBodyText: string;
    notifyOption: string;
    notificationRecepient: string;
    imageSize: string;
    imageLayout: string;
    numberOfFiles: number;
    showInTable: boolean;
    highlightRow: boolean;
    signatureByThirdParty: boolean;
    title: string;
    totalInTable: boolean;
    type: string;
    uploaderId: string;
    parentId?: number;
    subQuestions: Question[];
    alertType: string;
    allowBackDate: boolean;
    allowForwardDate: boolean;
    allowInternalContacts: boolean;
    allowStopNotification: boolean;
    stopNotificationColor: string;
    allowManualPlant: boolean;
    includePlantItems: boolean;
    allowHoursWorkEdit: boolean;
    allowNextServiceEdit: boolean;
    plantType?: string;
    allowQuestionOnCSVImport: boolean;
    allowUnique: boolean;
    allowUniqueAcrossAll: boolean;
    bodyCoreQuestions: {
        label: string;
        value: string;
        disabled: boolean;
    }[] = [];
    notificationContacts: NotificationContact[];
    logicSetups: LogicSetup[] = [];
    isViewOnlyInEdit: boolean;
    pageOrientation: string;
    grid: Grid;
    gridSummaryRatingRanges: GridSummaryRatingRange[];
}

export class Option {
    id?: number;
    questionId?: number;
    value: string;
    color?: string;
    disabled?: boolean;
    isDefault?: boolean;
    isDefaultDisabled?: boolean;
}

export class NotificationOption {
    id?: number;
    questionId?: number;
    days: number;
    subject: string;
    text: string;
}

export class NotificationAddRole {
    id?: number;
    questionId?: number;
    roleId: number;
}

export class NotificationUser {
    id?: number;
    questionId?: number;
    formId?: number;
    email: number;
    name: string;
    companyName: string;
}

export class NotificationContact {
    id?: number;
    questionId: number;
    userId: number;
}

export class NotificationEmbedContact {
    id?: number;
    formId: number;
    userId: number;
}

export class FormEmbedContact {
    id?: number;
    formId: number;
    userId: number;
}

export class Section {
    formType: string;
    id?: number;
    name: string;
}

export class FileNameFormatItem {
    label: string;
    static default =
        "setup:title|text: |field:entryNumber|text: - |datefield:created";
    type: FileNameFormatItemType;
    value: string;
}

export enum FileNameFormatItemType {
    Client = "client",
    DateField = "datefield",
    Field = "field",
    Project = "project",
    Setup = "setup",
    Text = "text",
}

export class BulkDocumentResponse {
    aggregateId: string;
    files: Document[];
    images: Document[];
}

export class FilterCoreQuestion {
    id: number;
    label: string;
    options: Option[];
}

export class LogicSetup {
    id: number;
    questionId: number;
    isVisible: boolean;
    logicQuestions: LogicQuestion[];
    logicOptions: LogicOption[];
    qrCodeAlertText: string;
}

export class LogicQuestion {
    id: number;
    logicSetupId: number;
    questionId: number;
}

export class LogicOption {
    id: number;
    logicSetupId: number;
    optionId: number;
}

export class FormLogicallyHiddenQuestion {
    id: number;
    formId: number;
    questionId: number;
}

export class FormGridCellAnswer {
    id: number;
    formId: number;
    gridCellQuestionId: number;
    gridCellId: number;
    answer: string;
}

export class FormGridSummary {
    id: number;
    formId: number;
    questionId: number;
    totalScore: number;
    maxScore: number;
    ratingPercentage: number;
    ratingLabel: string;
    overallComments: string;
}

export class Grid {
    id: number;
    questionId: number;
    totalColumns: number;
    totalRows: number;
    headers: GridHeader[];
    cells: GridCell[];

    static map(entity: any): Grid {
        if (!entity) return new Grid();
        return {
            id: entity.id ?? 0,
            questionId: entity.questionId ?? 0,
            totalColumns: entity.totalColumns ?? 0,
            totalRows: entity.totalRows ?? 0,
            headers: (entity.headers || []).map(GridHeader.map),
            cells: (entity.cells || []).map(GridCell.map)
        };
    }

    static merge(entity: Grid, value: Grid): void {
        if (!entity || !value) return;
        entity.id = value.id ?? entity.id;
        entity.questionId = value.questionId ?? entity.questionId;
        entity.totalColumns = value.totalColumns ?? entity.totalColumns;
        entity.totalRows = value.totalRows ?? entity.totalRows;
        entity.headers = (value.headers || []).map(header => GridHeader.map(header));
        entity.cells = (value.cells || []).map(cell => GridCell.map(cell));
    }
}

export class GridHeader {
    id: number;
    gridId: number;
    index: number;
    label: string;
    sumOfColumnLabel: string;
    displaySumOfColumn: boolean;
    width: number;

    static map(entity: any): GridHeader {
        if (!entity) return new GridHeader();
        return {
            id: entity.id ?? 0,
            gridId: entity.gridId ?? 0,
            index: entity.index ?? 0,
            label: entity.label ?? '',
            width: entity.width ?? 0,
            displaySumOfColumn: entity.displaySumOfColumn ?? false,
            sumOfColumnLabel: entity.sumOfColumnLabel ?? ''
        };
    }

    static merge(entity: GridHeader, value: GridHeader): void {
        if (!entity || !value) return;
        entity.id = value.id ?? entity.id;
        entity.gridId = value.gridId ?? entity.gridId;
        entity.index = value.index ?? entity.index;
        entity.label = value.label ?? entity.label;
        entity.width = value.width ?? entity.width;
        entity.displaySumOfColumn = value.displaySumOfColumn ?? entity.displaySumOfColumn;
        entity.sumOfColumnLabel = value.sumOfColumnLabel ?? entity.sumOfColumnLabel;
    }
}

export class GridCell {
    id: number;
    gridId: number;
    rowIndex: number;
    columnIndex: number;
    cellQuestion: GridCellQuestion;

    static map(entity: any): GridCell {
        if (!entity) return new GridCell();
        return {
            id: entity.id ?? 0,
            gridId: entity.gridId ?? 0,
            rowIndex: entity.rowIndex ?? 0,
            columnIndex: entity.columnIndex ?? 0,
            cellQuestion: GridCellQuestion.map(entity.cellQuestion)
        };
    }

    static merge(entity: GridCell, value: GridCell): void {
        if (!entity || !value) return;
        entity.id = value.id ?? entity.id;
        entity.gridId = value.gridId ?? entity.gridId;
        entity.rowIndex = value.rowIndex ?? entity.rowIndex;
        entity.columnIndex = value.columnIndex ?? entity.columnIndex;
        GridCellQuestion.merge(entity.cellQuestion, value.cellQuestion);
    }
}

export class GridCellQuestion {
    id: number;
    gridCellId: number;
    allowOther: boolean;
    allowBackDate: boolean;
    allowForwardDate: boolean;
    allowTodayDate: boolean;
    extendedType?: string;
    isRequired: boolean;
    label: string;
    type: string;
    cellQuestionOptions: GridCellQuestionOption[];

    static map(entity: any): GridCellQuestion {
        if (!entity) return new GridCellQuestion();
        return {
            id: entity.id ?? 0,
            gridCellId: entity.gridCellId ?? 0,
            allowOther: entity.allowOther ?? false,
            allowBackDate: entity.allowBackDate ?? false,
            allowForwardDate: entity.allowForwardDate ?? false,
            allowTodayDate: entity.allowTodayDate ?? false,
            extendedType: entity.extendedType ?? '',
            isRequired: entity.isRequired ?? false,
            label: entity.label ?? '',
            type: entity.type ?? '',
            cellQuestionOptions: entity.cellQuestionOptions?.map(GridCellQuestionOption.map) || []
        };
    }

    static merge(entity: GridCellQuestion, value: GridCellQuestion): void {
        if (!entity || !value) return;
        Object.assign(entity, {
            id: value.id,
            gridCellId: value.gridCellId,
            allowOther: value.allowOther,
            allowBackDate: value.allowBackDate,
            allowForwardDate: value.allowForwardDate,
            allowTodayDate: value.allowTodayDate,
            extendedType: value.extendedType,
            isRequired: value.isRequired,
            label: value.label,
            type: value.type,
        });
        entity.cellQuestionOptions = value.cellQuestionOptions?.map(GridCellQuestionOption.map) || entity.cellQuestionOptions;
    }
}

export class GridCellQuestionOption {
    id: number;
    gridCellQuestionId: number;
    value: string;

    static map(entity: any): GridCellQuestionOption {
        if (!entity) return new GridCellQuestionOption();
        return {
            id: entity.id ?? 0,
            gridCellQuestionId: entity.gridCellQuestionId ?? 0,
            value: entity.value ?? ''
        };
    }

    static merge(entity: GridCellQuestionOption, value: GridCellQuestionOption): void {
        if (!entity || !value) return;
        Object.assign(entity, {
            id: value.id,
            gridCellQuestionId: value.gridCellQuestionId,
            value: value.value
        });
    }
}

export interface GridStructure {
    id: number;
    headers: { id: number; gridId: number; index: number; label: string; width: string; displaySumOfColumn: boolean; sumOfColumnLabel: string }[];
    rows:
    {
        expanded: boolean;
        columns: {
            id: number;
            gridCellId: number;
            type: string;
            label: string;
            extendedType?: string;
            allowOther: boolean;
            allowBackDate: boolean;
            allowForwardDate: boolean;
            allowTodayDate: boolean;
            isRequired: boolean;
            cellQuestionOptions: { value: string, id: number, gridCellQuestionId: number }[]
        }[]
    }[]
}

export interface GridSummaryRatingRange {
    id: number;
    questionId: number;
    min: number;
    max: number;
    label: string;
}

